// src/components/admin/AdminReports.jsx

import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// Orchestrator Hook
import { useAdminReports } from '../../hooks/useAdminReports';

import {
  Box, Container, Typography, Paper, Button,
  Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Menu,
  MenuItem, Fade, Tooltip, Stack, Skeleton, Divider,
  TextField, InputAdornment, FormControl, Select, TableSortLabel
} from '@mui/material';

import FileDownloadIcon        from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon        from '@mui/icons-material/PictureAsPdf';
import KeyboardArrowDownIcon   from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon              from '@mui/icons-material/Search';

// ─── PDF Dynamic Imports ──────────────────────────────────────────────────────
let jsPDF;
let autoTable;
let loadingPDFPromise = null;

const loadPDFLibraries = async () => {
  if (loadingPDFPromise) return loadingPDFPromise;
  loadingPDFPromise = (async () => {
    try {
      const { default: jsPDFModule } = await import('jspdf');
      jsPDF = jsPDFModule;
      const autoTableModule = await import('jspdf-autotable');
      autoTable = autoTableModule.default;
    } catch (error) {
      console.warn('PDF export features uninitialized:', error.message);
    }
  })();
  return loadingPDFPromise;
};
loadPDFLibraries();

// ─── Formatting Utilities ────────────────────────────────────────────────────
const formatCurrency = (val) =>
  typeof val === 'number' ? `₹${val.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '₹0';

const formatDate = (dateVal) => {
  if (!dateVal) return 'N/A';
  if (typeof dateVal.toDate === 'function') return dateVal.toDate().toLocaleDateString('en-IN');
  const parsed = new Date(dateVal);
  return isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleDateString('en-IN');
};

const formatHeader = (keyName) => {
  const result = keyName.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

const isNumericValueField = (keyName) => {
  const k = keyName.toLowerCase();
  return k.includes('value') || k.includes('depreciation') || k.includes('cost');
};

const DETAILED_REPORTS_CONFIG = [
  { key: 'assetRegistry',          label: 'Asset Registry' },
  { key: 'assetAuditVerification', label: 'Asset Audit & Verification' },
  { key: 'unverifiedAssets',       label: 'Unverified Assets' },
  { key: 'missingAssets',          label: 'Missing Assets' },
  { key: 'assetMovement',          label: 'Asset Transfer History' },
  { key: 'depreciationRegister',   label: 'Depreciation Register' },
  { key: 'consumableStock',        label: 'Consumable Stock Ledger' },
  { key: 'expiringAssets',         label: 'Expiring Assets (90 Days)' },
  { key: 'expiredAssets',          label: 'Expired Assets' },
];

// ─── Skeleton ─────────────────────────────────────────────────────────────────
const AdminReportsSkeleton = () => (
  <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box>
        <Skeleton variant="text"    width={280} height={44} />
        <Skeleton variant="text"    width={300} height={20} />
      </Box>
      <Skeleton variant="rounded"   width={120} height={40} />
    </Box>

    <Box sx={{ borderRadius: 3, border: '1px solid #e0e0e0', background: '#fff', overflow: 'hidden' }}>
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Skeleton variant="text" width={160} height={28} />
          <Skeleton variant="text" width={180} height={16} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={150} height={36} />
          <Skeleton variant="rounded" width={250} height={36} />
        </Box>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', gap: 2, px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0', bgcolor: '#FAFBFC' }}>
        {[160, 200, 140, 120, 100, 80].map((w, i) => (
          <Skeleton key={i} variant="text" width={w} height={16} />
        ))}
      </Box>
      {Array.from({ length: 6 }, (_, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 2, px: 2, py: 1.5, borderBottom: '1px solid #f5f5f5', alignItems: 'center' }}>
          {[160, 200, 140, 120, 100, 80].map((w, j) => (
            <Skeleton key={j} variant="text" width={w} height={14} />
          ))}
        </Box>
      ))}
    </Box>
  </Container>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminReports = () => {
  const { currentUser } = useAuth();
  const location = useLocation();

  const { reportData, loading, error } = useAdminReports(currentUser);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchColumn, setSearchColumn] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const selectedDetailedReport = useMemo(() => {
    const pathParts = location.pathname.split('/');
    const currentKey = pathParts[pathParts.length - 1];
    const match = DETAILED_REPORTS_CONFIG.find(r => r.key === currentKey);
    return match ? match.key : 'assetRegistry';
  }, [location.pathname]);

  useEffect(() => {
    setPage(0);
    setSearchQuery('');
    setSearchColumn('all');
    setSortConfig({ key: null, direction: 'asc' });
  }, [selectedDetailedReport]);

  const rawDetailedRows = useMemo(() =>
    reportData?.detailedReports?.[selectedDetailedReport] || [],
    [reportData, selectedDetailedReport]
  );

  const detailedHeaders = useMemo(() =>
    rawDetailedRows.length > 0 ? Object.keys(rawDetailedRows[0]) : [],
    [rawDetailedRows]
  );

  const filteredDetailedRows = useMemo(() => {
    if (!searchQuery) return rawDetailedRows;
    const query = searchQuery.toLowerCase().trim();
    return rawDetailedRows.filter((row) => {
      if (searchColumn && searchColumn !== 'all') {
        const val = row[searchColumn];
        return String(val ?? '').toLowerCase().includes(query);
      }
      return Object.values(row).some((val) => String(val ?? '').toLowerCase().includes(query));
    });
  }, [rawDetailedRows, searchQuery, searchColumn]);

  const sortedDetailedRows = useMemo(() => {
    if (!sortConfig.key) return filteredDetailedRows;

    const { key: colKey, direction } = sortConfig;

    return [...filteredDetailedRows].sort((a, b) => {
      const aVal = a[colKey];
      const bVal = b[colKey];

      if (aVal === bVal) return 0;
      if (aVal == null) return direction === 'asc' ? 1 : -1;
      if (bVal == null) return direction === 'asc' ? -1 : 1;

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal);
      const bStr = String(bVal);

      const dateRegex = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
      if (dateRegex.test(aStr) && dateRegex.test(bStr)) {
        const [aD, aM, aY] = aStr.split('/');
        const [bD, bM, bY] = bStr.split('/');
        const dateA = new Date(`${aY}-${aM}-${aD}`).getTime();
        const dateB = new Date(`${bY}-${bM}-${bD}`).getTime();
        return direction === 'asc' ? dateA - dateB : dateB - dateA;
      }

      return direction === 'asc'
        ? aStr.localeCompare(bStr, undefined, { numeric: true })
        : bStr.localeCompare(aStr, undefined, { numeric: true });
    });
  }, [filteredDetailedRows, sortConfig]);

  const pagedDetailedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedDetailedRows.slice(start, start + rowsPerPage);
  }, [sortedDetailedRows, page, rowsPerPage]);

  const currentReportLabel = DETAILED_REPORTS_CONFIG.find(r => r.key === selectedDetailedReport)?.label || 'Detailed Report';

  const handleSort = (columnKey) => {
    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });
  };

  // ─── EXPORT CSV (ONLY TABLE DATA) ──────────────────────────────────────
  const exportCSV = () => {
    if (!reportData) return;

    const sections = [];

    // Only push the table data that is currently being viewed
    if (sortedDetailedRows.length > 0) {
      sections.push(currentReportLabel.toUpperCase());
      const headers = Object.keys(sortedDetailedRows[0]);
      sections.push(headers.map(formatHeader).join(','));
      for (const row of sortedDetailedRows) {
        sections.push(headers.map(h => `"${String(row[h] ?? '').replace(/"/g, '""')}"`).join(','));
      }
    } else {
      sections.push('No data available for this report.');
    }

    const blob = new Blob(['\uFEFF' + sections.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const safeName = String(reportData.company || 'Report').replace(/\s+/g, '_');
    link.download = `${safeName}_${currentReportLabel.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ─── EXPORT PDF (ONLY TABLE DATA) ──────────────────────────────────────
  const exportPDF = async () => {
    if (!reportData) return;
    setPdfGenerating(true);

    try {
      if (!jsPDF || !autoTable) await loadPDFLibraries();
      if (!jsPDF || !autoTable) {
        throw new Error('PDF libraries failed to load');
      }

      // Check if we need landscape based on columns
      const columnsToUse = sortedDetailedRows.length > 0 ? Object.keys(sortedDetailedRows[0]) : [];
      const useLandscape = columnsToUse.length > 5;
      
      const doc = new jsPDF(useLandscape ? 'l' : 'p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.width;

      // Draw Header Line & Title
      doc.setFillColor(21, 101, 192);
      doc.rect(0, 0, pageWidth, 6, 'F');
      
      doc.setFontSize(20); 
      doc.setTextColor(33, 33, 33);
      doc.text(String(reportData.company ?? 'Report'), 15, 22);
      
      doc.setFontSize(10); 
      doc.setTextColor(120);
      doc.text(`${currentReportLabel} • Generated ${formatDate(reportData.createdAt)}`, 15, 28);

      // Only draw the actual table data
      if (sortedDetailedRows.length > 0) {
        doc.setFontSize(9); 
        doc.setTextColor(120);
        doc.text(`${sortedDetailedRows.length} records`, 15, 34);

        autoTable(doc, {
          startY: 38, // Start table right below the header
          head: [columnsToUse.map(formatHeader)],
          body: sortedDetailedRows.map(row => columnsToUse.map(k => {
            const v = row[k];
            if (typeof v === 'number' && isNumericValueField(k)) return formatCurrency(v);
            return v ?? '';
          })),
          theme: 'striped',
          styles: { fontSize: 7, cellPadding: 1.5, overflow: 'linebreak' },
          headStyles: { fillColor: [21, 101, 192], fontSize: 7.5 },
        });
      } else {
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text("No data available for this report.", 15, 45);
      }

      const safeName = String(reportData.company || 'Report').replace(/\s+/g, '_');
      doc.save(`${safeName}_${currentReportLabel.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setPdfGenerating(false);
    }
  };

  if (loading) return <AdminReportsSkeleton />;
  if (error) return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
    </Container>
  );
  if (!reportData) return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Alert severity="warning" sx={{ borderRadius: 2 }}>No data available.</Alert>
    </Container>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ mb: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between"
               alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
              {reportData.company}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {reportData.contactPerson} • Generated {formatDate(reportData.createdAt)}
            </Typography>
          </Box>
          <Box>
            <Button
              variant="contained" disableElevation
              endIcon={<KeyboardArrowDownIcon />}
              startIcon={<FileDownloadIcon />}
              onClick={(e) => setExportAnchorEl(e.currentTarget)}
              disabled={pdfGenerating}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 2.5, py: 1 }}
            >
              {pdfGenerating ? 'Generating…' : 'Export Report'}
            </Button>
            <Menu
              anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)}
              onClose={() => setExportAnchorEl(null)}
              TransitionComponent={Fade}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { minWidth: 200, borderRadius: 2, mt: 0.5, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' } }}
            >
              <MenuItem onClick={() => { setExportAnchorEl(null); exportCSV(); }}>
                <FileDownloadIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                <Typography variant="body2">Download CSV</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => { setExportAnchorEl(null); exportPDF(); }}
                disabled={pdfGenerating}
              >
                <PictureAsPdfIcon fontSize="small" sx={{ mr: 1.5, color: 'error.main' }} />
                <Typography variant="body2">Download PDF</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Stack>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Box sx={{ p: 3, pb: 2 }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }} spacing={2} sx={{ mb: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>{currentReportLabel}</Typography>
              <Typography variant="caption" color="text.secondary">
                {filteredDetailedRows.length} of {rawDetailedRows.length} records
                {detailedHeaders.length > 0 && ` • ${detailedHeaders.length} columns`}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', lg: 'auto' } }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={searchColumn}
                  onChange={(e) => { setSearchColumn(e.target.value); setPage(0); }}
                  displayEmpty
                  sx={{ borderRadius: 2, bgcolor: '#FAFBFC' }}
                >
                  <MenuItem value="all">All Columns</MenuItem>
                  {detailedHeaders.map(h => (
                    <MenuItem key={h} value={h}>{formatHeader(h)}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder={searchColumn === 'all' ? `Search ${currentReportLabel}...` : `Search in ${formatHeader(searchColumn)}...`}
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                sx={{
                  flexGrow: 1,
                  minWidth: { xs: 'auto', sm: 250 },
                  '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#FAFBFC' }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Stack>
        </Box>

        <Divider />

        {pagedDetailedRows.length > 0 ? (
          <>
            <TableContainer sx={{ maxHeight: 620 }}>
              <Table size="small" stickyHeader sx={{ minWidth: detailedHeaders.length * 160 }}>
                <TableHead>
                  <TableRow>
                    {detailedHeaders.map(header => (
                      <TableCell key={header} sx={{
                        bgcolor: '#FAFBFC',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 1.75,
                      }}>
                        <TableSortLabel
                          active={sortConfig.key === header}
                          direction={sortConfig.key === header ? sortConfig.direction : 'asc'}
                          onClick={() => handleSort(header)}
                          sx={{
                            fontWeight: 700,
                            color: 'text.secondary',
                            fontSize: '0.72rem',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            whiteSpace: 'nowrap',
                            '&.Mui-active': { color: 'text.primary' },
                            '& .MuiTableSortLabel-icon': { color: 'text.primary !important' }
                          }}
                        >
                          {formatHeader(header)}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedDetailedRows.map((row, idx) => (
                    <TableRow key={idx} hover sx={{ '&:last-child td': { border: 0 } }}>
                      {detailedHeaders.map(header => {
                        const val = row[header];
                        const display = (typeof val === 'number' && isNumericValueField(header))
                          ? formatCurrency(val)
                          : String(val ?? '');
                        return (
                          <TableCell key={header} sx={{
                            whiteSpace: 'nowrap',
                            fontSize: '0.82rem',
                            maxWidth: 260,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            py: 1.5
                          }}>
                            <Tooltip title={display} arrow enterDelay={500}>
                              <span>{display}</span>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredDetailedRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
          </>
        ) : (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? 'No matching records found' : 'No data available for this report'}
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default AdminReports;